import { useEffect, useState } from 'react';
import { Button } from '@/client/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/client/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/client/components/ui/select';
import { trpc } from '@/client/utils/trpc';

function countWeekdays(from: string, to: string): number {
  if (!from || !to || from > to) return 0;
  let count = 0;
  const end = new Date(to);
  const cur = new Date(from);
  while (cur <= end) {
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

interface RequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RequestDialog({ open, onOpenChange }: RequestDialogProps) {
  const utils = trpc.useUtils();
  const [type, setType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setType('');
      setDateFrom('');
      setDateTo('');
      setNote('');
      setError(null);
    }
  }, [open]);

  const days = countWeekdays(dateFrom, dateTo);

  const mutation = trpc.timeoff.create.useMutation({
    onSuccess: () => {
      utils.timeoff.list.invalidate();
      utils.timeoff.myBalance.invalidate();
      onOpenChange(false);
      setType('');
      setDateFrom('');
      setDateTo('');
      setNote('');
      setError(null);
    },
    onError: e => setError(e.message),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!type) return setError('Please select a type.');
    if (!dateFrom || !dateTo) return setError('Please select a date range.');
    if (dateFrom > dateTo) return setError('Start date must be before end date.');
    if (days === 0) return setError('Selected range contains no weekdays.');
    setError(null);
    mutation.mutate({
      type: type as 'vacation' | 'sick' | 'personal' | 'remote',
      date_from: dateFrom,
      date_to: dateTo,
      days,
      note: note.trim() || undefined,
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Request time off</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 pt-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium">Type</label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger>
                <SelectValue placeholder="Select type…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="vacation">Vacation</SelectItem>
                <SelectItem value="sick">Sick leave</SelectItem>
                <SelectItem value="personal">Personal</SelectItem>
                <SelectItem value="remote">Remote</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium">To</label>
              <input
                type="date"
                value={dateTo}
                min={dateFrom}
                onChange={e => setDateTo(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
          </div>

          {dateFrom && dateTo && (
            <p className="text-xs text-muted-foreground">
              {days} weekday{days !== 1 ? 's' : ''} selected
            </p>
          )}

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium">
              Note{' '}
              <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Reason or details…"
              rows={2}
              className="flex w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Submitting…' : 'Submit request'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
